'use client';

import { useState, useEffect, useCallback, useId } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { AchievementEvent } from '@/lib/training/types';

export interface UseAchievementNotificationsReturn {
  latestEvent: AchievementEvent | null;
  events: AchievementEvent[];
  clearLatest: () => void;
}

export function useAchievementNotifications(
  userId: string | null,
): UseAchievementNotificationsReturn {
  const [events, setEvents] = useState<AchievementEvent[]>([]);
  const [latestEvent, setLatestEvent] = useState<AchievementEvent | null>(null);
  const instanceId = useId();

  useEffect(() => {
    setEvents([]);
    setLatestEvent(null);
    if (!userId) return;

    let active = true;
    const supabase = createClient();
    const channel = supabase
      .channel(`achievement-events-${instanceId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'achievement_events',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const event = payload.new as AchievementEvent;
          if (
            active &&
            event.event_type === 'achievement_unlocked' &&
            event.user_id === userId &&
            typeof event.id === 'string' &&
            event.id
          ) {
            setLatestEvent(event);
            // Call history consumes this collection too. Retain every unlock,
            // including events that arrive in the same React render batch.
            setEvents((prev) =>
              prev.some((existing) => existing.id === event.id) ? prev : [event, ...prev],
            );
          }
        },
      )
      .subscribe();

    return () => {
      active = false;
      supabase.removeChannel(channel);
    };
  }, [userId, instanceId]);

  const clearLatest = useCallback(() => setLatestEvent(null), []);

  return { latestEvent, events, clearLatest };
}
